import * as React from "react";
import {Link} from "react-router-dom";
import Slider, {Settings} from "react-slick";
import {IOccupationSkillTagRepository} from "src/domain/repository/skill_tag_repository";
import {createApiClient, IApiClient} from "src/infra/api/client";
import {entity} from "src/infra/api/rpc/api";
import {createOccupationSkillTagAPI} from "src/infra/api/service/skill_tag_api";
import GoldLink from "src/presentation/common/atom/btn/gold_link";
import styled from "styled-components";
import {ConstBreakPoint, ConstCalc, ConstColor,} from "../../../../config/styles_const/styles_const";
import {ClientBookmark} from "../../../../domain/model/client_bookmark";
import {Customer} from "../../../../domain/model/customer";
import {Occupation} from "../../../../domain/model/project";
import {TJobCareerWithCustomerID} from "../../../../domain/repository/job_career_repository";
import Assets from "../../../assets/img/assets";
import SlideNextArrow from "../../../common/atom/slider_arrow/slide_next_arrow";
import SlidePrevArrow from "../../../common/atom/slider_arrow/slide_prev_arrow";
import HeadlineSmall from "../../../common/atom/text/headline_small";
import Paragraph from "../../../common/atom/text/paragraph";
import ParagraphSup from "../../../common/atom/text/paragraph_sup";
import {TOccupationCounts} from "../../../type/common";
import SlideCustomerCard from "./slide_customer_card";

const apiClient: IApiClient = createApiClient();
const skillTagRepository: IOccupationSkillTagRepository =
    createOccupationSkillTagAPI(apiClient);

// プラン用の設定
const planSettings: Settings = {
    className: "slider",
    dots: false,
    infinite: false,
    centerMode: false,
    swipeToSlide: true,
    variableWidth: false,
    arrows: true,
    prevArrow: <SlidePrevArrow/>,
    nextArrow: <SlideNextArrow/>,
    slidesToShow: 4,
    slidesToScroll: 1,
    rows: 1,
    slidesPerRow: 1,
    responsive: [
        {
            breakpoint: 1024,
            settings: {
                slidesToShow: 3,
                slidesToScroll: 1,
                rows: 1,
                slidesPerRow: 1,
            },
        },
        {
            breakpoint: 650,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 1,
                rows: 1,
                slidesPerRow: 1,
            },
        },

        {
            breakpoint: 450,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                rows: 1,
                slidesPerRow: 1,
            },
        },
    ],
};

// breackpoint は調整必要
const settings: Settings = {
    className: "slider",
    dots: false,
    infinite: false,
    centerMode: false,
    swipeToSlide: true,
    variableWidth: true,
    arrows: true,
    prevArrow: <SlidePrevArrow/>,
    nextArrow: <SlideNextArrow/>,
    slidesToShow: 4,
    slidesToScroll: 4,
    responsive: [
        {
            breakpoint: 1024,
            settings: {
                slidesToShow: 3,
                slidesToScroll: 3,
            },
        },
        {
            breakpoint: 650,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 2,
            },
        },

        {
            breakpoint: 450,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
            },
        },
    ],
};

const bannerSettings: Settings = {
    className: "slider",
    dots: false,
    infinite: false,
    centerMode: false,
    swipeToSlide: true,
    variableWidth: true,
    arrows: true,
    prevArrow: <SlidePrevArrow/>,
    nextArrow: <SlideNextArrow/>,
    slidesToShow: 2,
    slidesToScroll: 2,
};

const byIssueArray: Array<{ title: string; query: string }> = [
    {
        title: "Webマーケティングの改善",
        query: "webマーケ",
    },
    {
        title: "営業組織の強化",
        query: "営業組織",
    },
    {
        title: "CS組織の立ち上げ",
        query: "カスタマーサクセス",
    },
    {
        title: "エンジニア組織の教育/評価改善",
        query: "エンジニア 教育",
    },
    {
        title: "社内データの活用強化",
        query: "データ活用",
    },
    {
        title: "SaaSビジネスの事業戦略改善",
        query: "SaaS 戦略",
    },
    {
        title: "中途採用の強化",
        query: "中途採用",
    },
    {
        title: "マネジメント層の教育",
        query: "マネジメント 教育",
    },
    {
        title: "株価向上の為のIR強化",
        query: "IR 株価",
    },
    {
        title: "社内DXの推進",
        query: "DX推進",
    },
];

const occupationObjArray: Array<{ occupation: Occupation; img: any }> = [
    {
        occupation: Occupation.Occupation_CorporateBusinessPlanning,
        img: Assets.corporate_business_planning,
    },
    {
        occupation: Occupation.Occupation_BusinessDevelopment,
        img: Assets.business_development,
    },
    {
        occupation: Occupation.Occupation_Marketing,
        img: Assets.marketing,
    },
    {
        occupation: Occupation.Occupation_PublicRelations,
        img: Assets.public_relations,
    },

    {
        occupation: Occupation.Occupation_WebMarketing,
        img: Assets.web_marketing,
    },
    {
        occupation: Occupation.Occupation_Sales,
        img: Assets.sales,
    },
    {
        occupation: Occupation.Occupation_HumanResource,
        img: Assets.human_resource,
    },
    {
        occupation: Occupation.Occupation_MinistryOfJustice,
        img: Assets.ministry_of_justice,
    },

    {
        occupation: Occupation.Occupation_InformationSystem,
        img: Assets.information_system,
    },
    {
        occupation: Occupation.Occupation_Engineer,
        img: Assets.engineer,
    },
    {
        occupation: Occupation.Occupation_IPO,
        img: Assets.ipo_occupation,
    },
    {
        occupation: Occupation.Occupation_Finance,
        img: Assets.finance,
    },
];

interface IProps {
    customers: Customer[];
    occupationCounts: TOccupationCounts | null;
    jobCareersWithCustomerID: TJobCareerWithCustomerID[];
    clientBookmarks: ClientBookmark[];
    handleSearch: (values: any) => void;
}

const ClientTopPageMain: React.FC<IProps> = (props) => {
    const {
        customers,
        occupationCounts,
        jobCareersWithCustomerID,
        clientBookmarks,
    } = props;

    const [categories, setCategories] = React.useState<
        entity.IOccupationSkillTagCategoryItem[]
    >([]);

    React.useEffect(() => {
        try {
            skillTagRepository.getAllCategory().then((items) => {
                setCategories(items);
            });
        } catch (e) {

        }
    }, []);

    const buildCategoryTag = (category: number) => {
        const target = categories.find((item) => item.category === category);
        if (!target) {
            return <></>;
        }

        const img = require(`../../../assets/img/ost_${category}.png`);

        return (
            <OccupationLink
                to={`/client/scout/customers?otc=${category}`}
                key={category}
            >
                <OccupationWrapper>
                    <OccupationImg src={img}/>
                    <OccupationName>
                        <ParagraphSup bold={true}>{target.name}</ParagraphSup>
                        <ParagraphSup>{target.customerNum}人</ParagraphSup>
                    </OccupationName>
                </OccupationWrapper>
            </OccupationLink>
        );
    };

    const planData = [
        {
            id: 1,
            type: 1,
            title: "アドバイザー",
            description: "エキスパートが伴走することで、様々な経営課題の解決を行います。"
        },
        {
            id: 2,
            type: 2,
            title: "コンサルティング",
            description: "エキスパートを中長期的に契約をして、経営戦略や事業計画など実働を依頼します。"
        },
        {
            id: 3,
            type: 3,
            title: "メンタリング",
            description: "特定の事業課題に関して数時間のメンタリングを通じて洞察を得ることができます。"
        },
        {
            id: 4,
            type: 4,
            title: "リサーチ",
            description: "マーケットや競合調査など専門領域のエキスパートや情報を調査できます。"
        },
        {
            id: 5,
            type: 5,
            title: "研修",
            description: "特定の領域やサービスなどにおける企業内研修などを実施可能です"
        },
    ];

    return (
        <>
            <SubTitleWrapper>
                <HeadlineSmall>プランを選択して人選を依頼する</HeadlineSmall>
            </SubTitleWrapper>

            <SliderWrapper2>
                <Slider {...planSettings}>
                    {planData.map((plan) => (
                        <SlidePlanWrapper key={plan.id}>
                            <PlanCardLink to={`/client/consultation/form?type=${plan.type}`}>
                                <PlanTitle>{plan.title}</PlanTitle>
                                <PlanDescription>{plan.description}</PlanDescription>
                            </PlanCardLink>
                        </SlidePlanWrapper>
                    ))}
                </Slider>
            </SliderWrapper2>

            <div style={{marginBottom: 40}}>
                <SubTitleWrapper>
                    <HeadlineSmall>特集</HeadlineSmall>
                </SubTitleWrapper>
                <div style={{display: "flex", flexWrap: "wrap"}}>
                    <PmBanner>
                        <Link to={"/client/pm_campaign"}>
                            <Img src={Assets.pm_campaign}/>
                        </Link>
                    </PmBanner>

                    <PmBanner>
                        <Link to={"/client/marketing_campaign"}>
                            <Img src={Assets.marketing_campaign}/>
                        </Link>
                    </PmBanner>

                    <PmBanner>
                        <Link to={"/client/sales_campaign"}>
                            <Img src={Assets.sales_campaign}/>
                        </Link>
                    </PmBanner>

                    <PmBanner>
                        <Link to={"/client/ipo_campaign"}>
                            <Img src={Assets.ipo_campaign}/>
                        </Link>
                    </PmBanner>
                </div>
            </div>

            <SubTitleWrapper>
                <HeadlineSmall>新着エキスパート</HeadlineSmall>
                <CustomerReadMore to={`/client/scout/customers`}>
                    もっと見る
                </CustomerReadMore>
            </SubTitleWrapper>

            <SliderWrapper>
                <Slider {...settings}>
                    {customers.map((customer) => {
                        const jobCareerWithCustomerID = jobCareersWithCustomerID.find(
                            (_jobCareerWithCustomerID) => {
                                return _jobCareerWithCustomerID.customerID === customer.id;
                            }
                        );
                        const jobCareers = jobCareerWithCustomerID
                            ? jobCareerWithCustomerID.jobCareers
                            : [];
                        const isBookmarked: boolean = clientBookmarks.some(
                            (clientBookmark: ClientBookmark) => {
                                return clientBookmark.customerID === customer.id;
                            }
                        );

                        return (
                            <SlideCustomerWrapper key={customer.id} style={{width: 200}}>
                                <SlideCustomerCard
                                    customer={customer}
                                    jobCareers={jobCareers}
                                    isBookmarked={isBookmarked}
                                />
                            </SlideCustomerWrapper>
                        );
                    })}
                </Slider>
            </SliderWrapper>

            <SubTitleWrapper>
                <HeadlineSmall>課題別でみる</HeadlineSmall>
            </SubTitleWrapper>

            <SearchWordWrapper>
                {byIssueArray.map((byIssue) => {
                    return (
                        <SearchWordLink
                            key={byIssue.title}
                            to={`/client/scout/customers?q=${byIssue.query}&p=1`}
                        >
                            <Paragraph bold={true}>{byIssue.title}</Paragraph>
                        </SearchWordLink>
                    );
                })}
            </SearchWordWrapper>

            <SubTitleWrapper>
                <HeadlineSmall>職種でみる</HeadlineSmall>
            </SubTitleWrapper>
            <OccupationFlex>
                {buildCategoryTag(1)}
                {buildCategoryTag(2)}
                {buildCategoryTag(3)}
                {buildCategoryTag(4)}
                {buildCategoryTag(15)}
                {buildCategoryTag(12)}
                {buildCategoryTag(11)}
                {buildCategoryTag(8)}
                {buildCategoryTag(16)}
                {buildCategoryTag(5)}
            </OccupationFlex>
        </>
    );
};

export default ClientTopPageMain;

const SubTitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    margin-bottom: 15px;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        margin-bottom: 15px;
    }
`;

const SearchWordWrapper = styled.div`
    margin-bottom: 30px;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        margin-bottom: 45px;
    }
`;

const SearchWordLink = styled(Link)`
    display: inline-block;
    background: ${ConstColor.White};
    box-shadow: 0px 0px 10px rgba(199, 199, 199, 0.2);
    border-radius: 10px;

    margin-right: 10px;
    margin-bottom: 10px;
    padding: 5px 10px;
    transition: all 0.3s ease;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        margin-right: 15px;
        margin-bottom: 15px;
        padding: 10px 30px;
    }

    &:hover {
        box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.4);
    }
`;

const OccupationLink = styled(Link)`
    margin: 0 15px 15px 0;
    width: calc(50% - 15px / 2);

    :nth-child(2n) {
        margin-right: 0px;
    }

    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        margin: 0 20px 20px 0;
        width: calc(25% - 20px * 3 / 4);
        :nth-child(2n) {
            margin-right: 20px;
        }

        :nth-child(4n) {
            margin-right: 0px;
        }
    }
`;

const OccupationFlex = styled.div`
    display: flex;
    flex-wrap: wrap;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
    }
`;

const OccupationWrapper = styled.div`
    box-shadow: 0px 0px 10px rgba(199, 199, 199, 0.2);
    border-radius: 10px;
    display: flex;
    transition: all 0.3s ease;

    &:hover {
        box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.4);
    }
`;

const OccupationImg = styled.img`
    height: 60px;
    width: 50%;
    object-fit: cover;
    border-radius: 10px 0 0 10px;
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        height: 64px;
    }
`;

const OccupationName = styled.div`
    padding: 0 10px;
    width: 50%;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const SliderWrapper = styled.div`
    margin: 0px auto;
    margin-bottom: 40px;

    // react-slickの横幅の計算がうまく働いていないのでここで決めている感じ
    max-width: ${ConstCalc.CustomerSlideMaxWidthSp};
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        margin-bottom: 50px;
        max-width: ${ConstCalc.CustomerSlideMaxWidthTablet};
    }
    @media screen and (min-width: ${ConstBreakPoint.PcDeskTop}) {
        max-width: ${ConstCalc.CustomerSlideMaxWidthPc};
    }

    // 一部で縦の大きさが変動していたため固定する

    & .slick-slider {
        height: 400px;
    }
`;

const SliderWrapper2 = styled.div`
    margin: 0px auto;
    margin-bottom: 40px;

    // react-slickの横幅の計算がうまく働いていないのでここで決めている感じ
    max-width: ${ConstCalc.CustomerSlideMaxWidthSp};
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        margin-bottom: 50px;
        max-width: ${ConstCalc.CustomerSlideMaxWidthTablet};
    }
    @media screen and (min-width: ${ConstBreakPoint.PcDeskTop}) {
        max-width: ${ConstCalc.CustomerSlideMaxWidthPc};
    }

    & .slick-list {
        overflow: hidden;
    }

    & .slick-track {
        display: flex !important;
        flex-wrap: nowrap !important;
    }

    & .slick-slide {
        flex-shrink: 0;
    }
`;


const SliderBannerWrapper = styled.div`
    margin-bottom: 40px;
    height: 150px;

    // react-slickの横幅の計算がうまく働いていないのでここで決めている感じ
    max-width: 670px;
`;

const SlideCustomerWrapper = styled.div`
    padding: 5px 15px;
    display: flex !important;
    height: 100%;
`;

const SlidePlanWrapper = styled.div`
    padding: 5px 15px;
    box-sizing: border-box;
`;

const SlideBannerWrapper = styled.div`
    padding: 5px 15px;
`;

const PmBanner = styled.div`
    width: 300px;
    margin-right: 14px;
    margin-bottom: 14px;
`;

const Img = styled.img`
    width: 100%;
`;

const CustomerReadMore = styled(GoldLink)`
    font-size: 12px;
`;

const PlanCardLink = styled(Link)`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    background: ${ConstColor.White};
    box-shadow: 0px 0px 10px rgba(199, 199, 199, 0.2);
    border-radius: 20px;
    padding: 30px 20px;
    height: 100%;
    position: relative;
    text-decoration: none;
    transition: transform 0.2s, box-shadow 0.2s;
    cursor: pointer;
    color: #333;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
    }
`;

const PlanTitle = styled.h3`
    font-size: 12px;
    font-weight: bold;
    margin-top: 0;
    margin-bottom: 15px;
    position: relative;
    padding-bottom: 10px;

    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 150px;
        height: 3px;
        background: #CAA239;
    }
`;

const PlanDescription = styled.p`
    font-size: 12px;
    line-height: 1.8;
    color: #333;
    margin: 0;
    text-align: center;
`;




// WEBPACK FOOTER //
// ./src/presentation/component/client/top_page/client_top_page_main.tsx